/**
 * lifecycleSubscribers.js — RC 1.53 Phase 4.2.1
 *
 * Module-level subscribers to the OperationLifecycleController's
 * event bus. Phase 4.2.1 wires ONE subscriber: a cache-invalidation
 * trigger that fires on `operation:complete` so the Documents
 * library auto-refreshes the relevant tab without a hard refresh.
 *
 * Wallet refresh remains owned by the section (defence-in-depth —
 * the section already calls `credits.refresh()` post-finalize-charge,
 * and Phase 4.2.1 explicitly does NOT touch `creditsContext`).
 *
 * Hard rails:
 *   • READ-ONLY for billing surfaces. NO credit-context mutation.
 *   • NO backend touch.
 *   • Idempotent — repeated subscribers are not stacked; the module
 *     uses a single `_initialised` guard.
 *   • Safe in SSR / Jest — guards typeof window.
 */

import { subscribe, OPERATION_EVENT_TYPES } from './operationEvents.js';
import { trace } from './actionTrace.js';

let _initialised = false;
let _unsubs = [];

/**
 * Registry: actionCode → list of cache-invalidator callbacks.
 *
 * Other layers (e.g. a future Documents-library context) can call
 * `registerCacheInvalidator('cv_variant', fn)` to be notified on
 * terminal-complete. Phase 4.2.1 ships an empty registry — sections
 * still update their own state today. The registry is here for
 * Phase 4.3 wiring (listCache → library tabs) and to give the
 * Documents library a single subscribe point.
 */
const _invalidators = new Map();

export function registerCacheInvalidator(actionCode, fn) {
  if (!actionCode || typeof fn !== 'function') return () => {};
  if (!_invalidators.has(actionCode)) _invalidators.set(actionCode, new Set());
  _invalidators.get(actionCode).add(fn);
  return () => {
    const set = _invalidators.get(actionCode);
    if (set) set.delete(fn);
  };
}

function _fireInvalidators(actionCode, detail) {
  const set = _invalidators.get(actionCode);
  if (!set) return;
  for (const fn of set) {
    try { fn(detail); } catch (e) {
      // Never let a misbehaving subscriber crash the event bus.
      // eslint-disable-next-line no-console
      console.warn('[lifecycleSubscribers] invalidator threw', e);
    }
  }
}

/**
 * Initialise the singleton subscribers. Safe to call repeatedly —
 * the guard ensures only one subscriber per event type. Returns a
 * teardown function (used by tests).
 */
export function initLifecycleSubscribers() {
  if (_initialised) return _teardown;
  _initialised = true;

  _unsubs.push(subscribe(OPERATION_EVENT_TYPES.COMPLETE, (detail) => {
    trace(detail?.actionCode || 'unknown', 'lifecycle_complete_event', {
      jobId: detail?.jobId,
      runId: detail?.runId,
    });
    _fireInvalidators(detail?.actionCode, detail);
  }));

  _unsubs.push(subscribe(OPERATION_EVENT_TYPES.FAIL, (detail) => {
    trace(detail?.actionCode || 'unknown', 'lifecycle_fail_event', {
      jobId: detail?.jobId,
      error: detail?.error,
    });
  }));

  return _teardown;
}

function _teardown() {
  _unsubs.forEach((u) => { try { u && u(); } catch { /* noop */ } });
  _unsubs = [];
  _initialised = false;
}

/** Test-only. */
export function _resetSubscribersForTests() {
  _teardown();
  _invalidators.clear();
}

export function _hasInvalidatorForTests(actionCode) {
  return (_invalidators.get(actionCode) || new Set()).size > 0;
}
